import React, { useState, useEffect } from 'react';
import type { Monitor, ScalingPreset } from '../../../types/Monitor';
import type { UserMonitorConfig } from '../../../types/UserMonitorConfig';
import { Desktop } from '../../macos/Desktop';
import { supportsHiDPIScaling } from '../../../utils/formatters';
import { getPresetsForMonitor, getDefaultPreset } from '../../../utils/scalingMode';
import styles from './ScalePreviewPanel.module.css';

interface ScalePreviewPanelProps {
  monitor: Monitor | null;
  userConfig: UserMonitorConfig;
}

export function ScalePreviewPanel({ monitor, userConfig }: ScalePreviewPanelProps) {
  const [selectedPreset, setSelectedPreset] = useState<ScalingPreset | null>(null);

  // Recompute presets and reset selection whenever the monitor changes
  useEffect(() => {
    if (!monitor) {
      setSelectedPreset(null);
      return;
    }
    const supportsScaling = supportsHiDPIScaling(monitor);
    if (!supportsScaling) {
      setSelectedPreset({ label: 'Native', logicalW: monitor.nativeResolutionW, logicalH: monitor.nativeResolutionH });
      return;
    }
    const presets = getPresetsForMonitor(monitor);
    setSelectedPreset(getDefaultPreset(presets));
  }, [monitor]);

  // Effective preset — falls back to native if somehow null
  const effectivePreset: { logicalW: number; logicalH: number } = selectedPreset ?? {
    logicalW: monitor?.nativeResolutionW ?? 1920,
    logicalH: monitor?.nativeResolutionH ?? 1080,
  };

  return (
    <div className={styles.panel}>
      {/* Empty state */}
      {!monitor && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon} aria-hidden="true">
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
              <rect x="4" y="10" width="48" height="32" rx="4" stroke="currentColor" strokeWidth="2" />
              <path d="M20 42h16M28 42v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              <circle cx="28" cy="26" r="8" stroke="currentColor" strokeWidth="1.5" strokeDasharray="3 2" />
              <path d="M24 26h8M28 22v8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <p className={styles.emptyTitle}>No monitor selected</p>
          <p className={styles.emptyText}>
            Click <strong>Preview</strong> on any monitor card to see the
            real-scale desktop.
          </p>
        </div>
      )}

      {/* Preview canvas */}
      {monitor && (() => {
        const supportsScaling = supportsHiDPIScaling(monitor);
        const presets = supportsScaling
          ? getPresetsForMonitor(monitor)
          : [{ label: 'Native', logicalW: monitor.nativeResolutionW, logicalH: monitor.nativeResolutionH }];

        return (
          <>
            {/* Scaling preset strip — show for HiDPI monitors with more than one preset */}
            {supportsScaling && presets.length > 1 && (
              <div className={styles.scalingModes}>
                {/* Button strip — hidden on narrow viewports */}
                <div className={styles.scalingModeButtons} role="group" aria-label="Scaling mode">
                  {presets.map((preset) => {
                    const isActive =
                      selectedPreset?.logicalW === preset.logicalW &&
                      selectedPreset?.logicalH === preset.logicalH;
                    return (
                      <button
                        key={`${preset.logicalW}x${preset.logicalH}`}
                        className={`${styles.modeBtn} ${isActive ? styles.modeBtnActive : ''}`}
                        onClick={() => setSelectedPreset(preset)}
                        aria-pressed={isActive}
                      >
                        <span className={styles.modeBtnLabel}>
                          {preset.label || `${preset.logicalW}×${preset.logicalH}`}
                        </span>
                        <span className={styles.modeBtnRes}>
                          looks like {preset.logicalW}×{preset.logicalH}
                        </span>
                      </button>
                    );
                  })}
                </div>

                {/* Dropdown — shown on narrow viewports */}
                <select
                  className={styles.scalingModeSelect}
                  aria-label="Scaling mode"
                  value={`${selectedPreset?.logicalW}x${selectedPreset?.logicalH}`}
                  onChange={(e) => {
                    const found = presets.find(
                      (p) => `${p.logicalW}x${p.logicalH}` === e.target.value
                    );
                    if (found) setSelectedPreset(found);
                  }}
                >
                  {presets.map((preset) => (
                    <option
                      key={`${preset.logicalW}x${preset.logicalH}`}
                      value={`${preset.logicalW}x${preset.logicalH}`}
                    >
                      {preset.label || `${preset.logicalW}×${preset.logicalH}`} — looks like {preset.logicalW}×{preset.logicalH}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Desktop canvas area */}
            <div className={styles.previewArea}>
              <Desktop
                monitor={monitor}
                userConfig={userConfig}
                scalingPreset={effectivePreset}
              />
            </div>
          </>
        );
      })()}
    </div>
  );
}

